import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins, Loader2, Lock, ListChecks, ChevronDown } from 'lucide-react';
import api from '../services/api';
import { WeekView, mmHeaders } from '../components/money-moves/WeekView';
import { ProjectCard, PeriodTest } from '../components/money-moves/PeriodCapstone';

const Standards = ({ standards }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-2 border-zinc-800 mb-8" data-testid="mm-standards">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 text-xs font-black uppercase tracking-[0.25em] text-white" data-testid="mm-standards-toggle">
        <span className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-emerald-400" /> Course Standards ({standards.length})</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 grid md:grid-cols-2 gap-x-6 gap-y-2">
          {standards.map((s) => <div key={s.code} className="text-xs text-zinc-300"><b className="text-emerald-400">{s.code}</b> <span className="text-zinc-500">{s.strand} ·</span> {s.text}</div>)}
        </div>
      )}
    </div>
  );
};

const Locked = ({ navigate }) => (
  <div className="border-2 border-emerald-500/40 p-8 text-center" data-testid="mm-locked">
    <Lock className="w-8 h-8 text-emerald-400 mx-auto" />
    <h2 className="text-xl font-black text-white mt-3">Unlock Money Moves</h2>
    <p className="text-sm text-zinc-400 mt-2">Money Moves is a paid elective. Unlock it from the Electives catalog, or ask Coach B.</p>
    <button onClick={() => navigate('/academy/elective-courses')} className="mt-5 bg-emerald-500 hover:bg-emerald-400 text-black px-6 py-3 text-[11px] font-black uppercase tracking-widest" data-testid="mm-unlock-btn">See Electives</button>
  </div>
);

export default function MoneyMoves() {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [period, setPeriod] = useState(1);
  const [week, setWeek] = useState(null);

  useEffect(() => {
    api.get('/academy/money-moves/course', { headers: mmHeaders() }).then((r) => setC(r.data)).catch((e) => {
      if (e?.response?.status === 401) navigate('/academy/login');
      else setTimeout(() => window.location.reload(), 5000);
    });
  }, [navigate]);

  const onScored = useCallback((key, best) => setC((x) => ({ ...x, progress: { ...x.progress, lessons: { ...x.progress.lessons, [key]: best } } })), []);
  const onProject = useCallback((p, v) => setC((x) => ({ ...x, progress: { ...x.progress, projects: { ...x.progress.projects, [p]: v } } })), []);
  const onTest = useCallback((p, s) => setC((x) => ({ ...x, progress: { ...x.progress, tests: { ...x.progress.tests, [p]: s } } })), []);

  if (!c) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;
  const P = c.periods.find((p) => p.n === period);
  const weeks = c.weeks.filter((w) => w.period === period);
  const done = (w) => [1, 2, 3, 4, 5].filter((d) => c.progress.lessons[`${w}-${d}`] !== undefined).length;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="money-moves-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button onClick={() => navigate('/academy/school')} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white mb-6" data-testid="mm-back-btn"><ArrowLeft className="w-3.5 h-3.5" /> Dashboard</button>
        <div className="border-b-2 border-emerald-500/30 pb-8 mb-8">
          <div className="flex items-center gap-2 text-[10px] font-black tracking-[0.35em] uppercase text-emerald-400"><Coins className="w-4 h-4" /> Full-Year Elective · Authored by {c.author}</div>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mt-2">{c.title}</h1>
          <p className="text-zinc-400 text-sm mt-2">{c.subtitle}</p>
          <div className="mt-6 max-w-3xl border-l-4 border-yellow-500 pl-4" data-testid="mm-theme">
            <div className="text-sm font-black text-yellow-400">{c.theme}</div>
            <p className="text-sm text-zinc-300 mt-1 leading-relaxed">{c.theme_blurb}</p>
          </div>
        </div>
        <Standards standards={c.standards} />
        {!c.access ? <Locked navigate={navigate} /> : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-6" data-testid="mm-period-tabs">
              {c.periods.map((p) => (
                <button key={p.n} onClick={() => { setPeriod(p.n); setWeek(null); }} className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest border-2 transition-colors ${period === p.n ? 'bg-emerald-500 border-emerald-500 text-black' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'}`} data-testid={`mm-period-${p.n}`}>
                  Period {p.n} · {p.pct}%
                </button>
              ))}
            </div>
            <h2 className="text-lg font-black uppercase tracking-tight">Period {P.n}: {P.title}</h2>
            <p className="text-xs text-zinc-500 mb-5">{P.focus}</p>
            {week ? (
              <div>
                <button onClick={() => setWeek(null)} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white mb-4" data-testid="mm-week-back"><ArrowLeft className="w-3.5 h-3.5" /> Period {P.n} weeks</button>
                <div className="text-[10px] font-black tracking-[0.3em] uppercase text-zinc-500">Week {week}</div>
                <h3 className="text-xl font-black mb-4">{c.weeks.find((w) => w.n === week).title}</h3>
                <WeekView week={week} progress={c.progress.lessons} onScored={onScored} />
              </div>
            ) : (
              <div className="grid lg:grid-cols-[1fr_380px] gap-6">
                <div className="space-y-2">
                  {weeks.map((w) => (
                    <button key={w.n} onClick={() => setWeek(w.n)} className="w-full flex items-center justify-between gap-3 border-2 border-zinc-800 hover:border-emerald-500/60 px-4 py-3 text-left transition-colors" data-testid={`mm-week-card-${w.n}`}>
                      <span><span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Week {w.n} · {w.standards.join(', ')}</span><span className="block text-sm font-bold text-white">{w.title}</span></span>
                      <span className="text-[11px] font-black text-emerald-400 shrink-0">{done(w.n)}/5</span>
                    </button>
                  ))}
                </div>
                <div className="space-y-4">
                  <ProjectCard period={P} saved={c.progress.projects[P.n] || c.progress.projects[String(P.n)]} onSaved={onProject} />
                  <PeriodTest key={P.n} period={P} onDone={onTest} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
